import React, { createContext, useContext, useState, ReactNode } from 'react'
import { logger } from '@/utils/logger'

interface ImageItem {
  id: string
  image_id: string
  filename: string
  image_type: string
  image_format: string
  width: number
  height: number
  magnification: number
  slide_id: string
  case_id: string
  file_size_mb: number
  resolution_mpx: number
  annotations_count: number
  created_at: string
  scanner_model?: string
  scan_date?: string
  thumbnail_url?: string
  status: 'processing' | 'ready' | 'error'
}

interface ImageContextType {
  images: ImageItem[]
  addImage: (image: ImageItem) => void
  updateImage: (id: string, updates: Partial<ImageItem>) => void
  getImage: (id: string) => ImageItem | undefined
  refreshImages: (backendImages?: ImageItem[]) => void
}

const ImageContext = createContext<ImageContextType | undefined>(undefined)

export const useImages = () => {
  const context = useContext(ImageContext)
  if (context === undefined) {
    throw new Error('useImages must be used within an ImageProvider')
  }
  return context
}

interface ImageProviderProps {
  children: ReactNode
}

export const ImageProvider: React.FC<ImageProviderProps> = ({ children }) => {
  // Start with empty array - load real data from backend
  const [images, setImages] = useState<ImageItem[]>([])

  const addImage = (image: ImageItem) => {
    logger.debug('Adding image to context:', image)
    setImages(prev => {
      const updated = [image, ...prev]
      logger.debug('Updated images array:', updated)
      return updated
    })
  }

  const updateImage = (id: string, updates: Partial<ImageItem>) => {
    setImages(prev => prev.map(img =>
      img.id === id ? { ...img, ...updates } : img
    ))
  }

  const getImage = (id: string) => {
    return images.find(img => img.id === id)
  }

  const refreshImages = (backendImages?: ImageItem[]) => {
    if (backendImages) {
      logger.debug('Refreshing images with backend data:', backendImages.length)
      setImages(backendImages)
    } else {
      logger.debug('Refreshing images (keeping current state)')
    }
  }

  const value = {
    images,
    addImage,
    updateImage,
    getImage,
    refreshImages
  }

  return (
    <ImageContext.Provider value={value}>
      {children}
    </ImageContext.Provider>
  )
}

export default ImageContext